import { createApi } from "@reduxjs/toolkit/dist/query/react";
import { baseQueryWithClient } from "@/redux/baseQuery";
import {
  ClientScopeArg,
  FavouritePassenger,
  ListFavouritePassengersRawResponse,
  RemoveFavouritePassengerArgs,
  SaveFavouritePassengerArgs,
} from "./types";

/**
 * Favourite guest passengers — served by the v3 client-scoped route
 * /v3/client/{accountId}/favourite-passenger, which serves both a client on their own account
 * (self-service) and an operator acting on a managed client (pass `accountId`). HAL shape
 * (`_embedded.passenger`) is unchanged.
 */
export const favouritePassengerApi = createApi({
  reducerPath: "favouritePassengerApi",
  baseQuery: baseQueryWithClient(""),
  tagTypes: ["FavouritePassenger"],
  endpoints: (builder) => ({
    listFavouritePassengers: builder.query<FavouritePassenger[], ClientScopeArg | void>({
      // `all` bypasses the route's page size, which the pickers need because they render the whole
      // list and sort/paginate it themselves. Same flag `listClientPois` passes for the same reason;
      // without it a client with more than 10 saved guests only ever saw the oldest 10 (DEV-1601).
      query: (arg) => ({ url: "/favourite-passenger", accountId: arg?.accountId, params: { all: true } }),
      transformResponse: (r: ListFavouritePassengersRawResponse) => r?._embedded?.passenger ?? [],
      providesTags: ["FavouritePassenger"],
    }),
    addFavouritePassenger: builder.mutation<FavouritePassenger, SaveFavouritePassengerArgs>({
      query: ({ accountId, ...body }) => ({ url: "/favourite-passenger", method: "POST", accountId, body }),
      invalidatesTags: ["FavouritePassenger"],
    }),
    updateFavouritePassenger: builder.mutation<FavouritePassenger, SaveFavouritePassengerArgs & { uuid: string }>({
      query: ({ uuid, accountId, ...body }) => ({ url: `/favourite-passenger/${uuid}`, method: "PUT", accountId, body }),
      invalidatesTags: ["FavouritePassenger"],
    }),
    removeFavouritePassenger: builder.mutation<void, RemoveFavouritePassengerArgs>({
      query: ({ passengerId, accountId }) => ({ url: `/favourite-passenger/${passengerId}`, method: "DELETE", accountId }),
      invalidatesTags: ["FavouritePassenger"],
    }),
  }),
});

export const {
  useListFavouritePassengersQuery,
  useAddFavouritePassengerMutation,
  useUpdateFavouritePassengerMutation,
  useRemoveFavouritePassengerMutation,
} = favouritePassengerApi;
